import { ComponentProps } from 'react';

import { VIDEO_FORMAT, Video } from '@sorare/core/src/atoms/ui/Video';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import introLoop from 'assets/lobby/16x9/intro-loop.webm';
import introOut from 'assets/lobby/16x9/intro-out.webm';
import introToPromotion from 'assets/lobby/16x9/intro-to-promotion.webm';
import introToRelegation from 'assets/lobby/16x9/intro-to-relegation.webm';
import promotionOut from 'assets/lobby/16x9/promotion-out.webm';
import promotionToRelegation from 'assets/lobby/16x9/promotion-to-relegation.webm';
import relegationOut from 'assets/lobby/16x9/relegation-out.webm';
import toIntro from 'assets/lobby/16x9/to-intro.webm';
import introLoopMobile from 'assets/lobby/9x16/intro-loop.webm';
import introOutMobile from 'assets/lobby/9x16/intro-out.webm';
import introToPromotionMobile from 'assets/lobby/9x16/intro-to-promotion.webm';
import introToRelegationMobile from 'assets/lobby/9x16/intro-to-relegation.webm';
import promotionOutMobile from 'assets/lobby/9x16/promotion-out.webm';
import promotionToRelegationMobile from 'assets/lobby/9x16/promotion-to-relegation.webm';
import relegationOutMobile from 'assets/lobby/9x16/relegation-out.webm';
import toIntroMobile from 'assets/lobby/9x16/to-intro.webm';

const DESKTOP_VIDEOS = {
  introLoop,
  toIntro,
  introOut,

  introToPromotion,
  introToRelegation,
  promotionOut,
  promotionToRelegation,
  relegationOut,
};

const MOBILE_VIDEOS = {
  introLoop: introLoopMobile,
  toIntro: toIntroMobile,
  introOut: introOutMobile,
  introToPromotion: introToPromotionMobile,
  introToRelegation: introToRelegationMobile,
  promotionOut: promotionOutMobile,
  promotionToRelegation: promotionToRelegationMobile,
  relegationOut: relegationOutMobile,
};

const MOBILE_VIDEOS_MP4 = {
  introLoop:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-loop.mp4',
  introOut:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-out.mp4',
  introToPromotion:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-to-promotion.mp4',
  introToRelegation:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/intro-to-relegation.mp4',
  promotionOut:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/promotion-out.mp4',
  promotionToRelegation:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/promotion-to-relegation.mp4',
  relegationOut:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/relegation-out.mp4',
  toIntro:
    'https://frontend-assets.sorare.com/football/lobby/fixture-story/to-intro.mp4',
};

const desktopVideosSources = Object.entries(DESKTOP_VIDEOS).reduce(
  (acc, [key, src]) => {
    acc[key as keyof typeof DESKTOP_VIDEOS] = [
      {
        src,
        type: VIDEO_FORMAT.WEBM,
      },
    ];
    return acc;
  },
  {} as {
    [key in keyof typeof DESKTOP_VIDEOS]: ComponentProps<
      typeof Video
    >['sources'];
  }
);

const mobileVideoSources = Object.entries(MOBILE_VIDEOS).reduce(
  (acc, [key, src]) => {
    acc[key as keyof typeof MOBILE_VIDEOS] = [
      {
        src,
        type: VIDEO_FORMAT.WEBM,
      },
      {
        src: MOBILE_VIDEOS_MP4[key as keyof typeof MOBILE_VIDEOS],
        type: VIDEO_FORMAT.MP4,
      },
    ];
    return acc;
  },
  {} as {
    [key in keyof typeof MOBILE_VIDEOS]: ComponentProps<
      typeof Video
    >['sources'];
  }
);

export const useResultsStoryAssets = () => {
  const isTabletAndAbove = useIsTabletAndAbove();
  return isTabletAndAbove ? desktopVideosSources : mobileVideoSources;
};
